import React, { useState, useMemo } from 'react';
import { ReactComponent as HeatMapLayoutIcon } from '../../assets/svg/heatmap-layout.svg';
import './HeatMapLayout.css';
import { Empty } from 'antd';

interface DamageItem {
  damage: string;
  damage_count: number;
}

interface DamageItems {
  totalCount: number;
  items?: DamageItem[];
}

interface DamageData {
  part: string;
  items: DamageItems;
}

interface DummyDamage {
  id: string;
  label: string;
  x: number;
  y: number;
  severity: string;
}

interface HeatMapLayoutProps {
  data?: DamageData[];
  divident?: number;
  loading?: boolean;
}

const HeatMapLayout: React.FC<HeatMapLayoutProps> = ({ data = [], divident = 1, loading = false }) => {
  const [activeDot, setActiveDot] = useState<number | null>(null);
  console.log('dataas', data);
  // ✅ O(1) lookup map (BIG PERFORMANCE BOOST)
  const dataMap = useMemo(() => {
    const map: Record<string, DamageData> = {};
    data.forEach(d => {
      map[d.part] = d;
    });
    return map;
  }, [data]);

  const toggleTooltip = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setActiveDot(prev => (prev === index ? null : index));
  };

  if (loading) {
    return <div className="heatmap-wrapper" />;
  }

  if (!data.length) {
    return <Empty description="No Damages found for this VIN" className="flex flex-col items-center justify-center h-full" />;
  }

  return (
    <div className="heatmap-wrapper" onClick={() => setActiveDot(null)}>
      <HeatMapLayoutIcon className="heatmap-svg" />

      {dummyDamages.map((d, i) => {
        const damageData = dataMap[d.id]; // ✅ FAST lookup

        if (!damageData) return null;

        const total = damageData.items.totalCount || 0;

        const size = Math.max((total / divident) * 15, 5);

        return (
          <div key={i}>
            {/* DOT */}
            <div
              className="heatmap-dot"
              onClick={e => toggleTooltip(e, i)}
              style={{
                top: `${d.y}%`,
                left: `${d.x}%`,
                width: `${size}px`,
                height: `${size}px`,
              }}
            />

            {/* TOOLTIP */}
            {activeDot === i && (
              <div
                className="tooltip-content"
                style={{
                  top: '5%',
                  left: `${d.x}%`,
                  transform: d.x > 50 ? 'translateX(-105%)' : 'translateX(10px)',
                }}>
                <div className="tooltip-header">Damage Details</div>

                <ul className="damage-list">
                  <li>
                    <strong>Position:</strong> {d.label} -<strong> {total}</strong>
                  </li>

                  {damageData.items.items?.map((dmg, k) => (
                    <li key={k} style={{ marginLeft: 20 }}>
                      {dmg.damage}: <strong>{dmg.damage_count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HeatMapLayout;

const dummyDamages: DummyDamage[] = [
  // { id: '03', label: 'BUMPER_COVER_EXTERIOR_FRONT', x: 27, y: 16, severity: 'medium' },
  { id: '05', label: 'BUMPER_COVER_EXTERIOR_FRONT', x: 21, y: 35, severity: 'medium' },
  { id: '03', label: 'BUMPER_COVER_EXTERIOR_FRONT', x: 27, y: 85, severity: 'medium' },
  { id: '22', label: 'GRILLE', x: 21, y: 50, severity: 'low' },
  { id: '24', label: 'HEADLIGHT_COVER_TURN_SIGNAL_LEFT', x: 24, y: 68, severity: 'medium' },
  { id: '24', label: 'HEADLIGHT_COVER_TURN_SIGNAL_RIGHT', x: 24, y: 33, severity: 'low' },
  { id: '14', label: 'FENDER_LEFT_FRONT', x: 35, y: 78, severity: 'medium' },
  { id: '16', label: 'FENDER_RIGHT_FRONT', x: 35, y: 22, severity: 'high' },
  { id: '10', label: 'DOOR_LEFT_FRONT', x: 45, y: 80, severity: 'low' },
  { id: '11', label: 'DOOR_LEFT_REAR', x: 60, y: 80, severity: 'medium' },
  { id: '08', label: 'DOOR_LEFT_REAR', x: 60, y: 80, severity: 'medium' },
  { id: '12', label: 'DOOR_RIGHT_FRONT', x: 45, y: 20, severity: 'medium' },
  { id: '13', label: 'DOOR_RIGHT_REAR', x: 60, y: 20, severity: 'low' },
  { id: '07', label: 'DOOR_RIGHT_REAR', x: 60, y: 20, severity: 'low' },
  { id: '30', label: 'MIRROR_OUTSIDE_LEFT', x: 42, y: 74, severity: 'low' },
  { id: '31', label: 'MIRROR_OUTSIDE_RIGHT', x: 42, y: 26, severity: 'low' },
  { id: '27', label: 'HOOD', x: 27, y: 50, severity: 'medium' },
  { id: '37', label: 'ROOF', x: 60, y: 50, severity: 'high' },
  { id: '20', label: 'GLASS_WINDSHIELD', x: 38, y: 50, severity: 'low' },
  { id: '21', label: 'GLASS_REAR', x: 72, y: 50, severity: 'medium' },
  { id: '45', label: 'TAIL_LIGHT_HARDWARE', x: 75, y: 35, severity: 'high' },
  { id: '45', label: 'TAIL_LIGHT_HARDWARE', x: 75, y: 65, severity: 'medium' },
  { id: '04', label: 'BUMPER_COVER_EXTERIOR_REAR', x: 82, y: 50, severity: 'medium' },
  { id: '06', label: 'BUMPER_COVER_EXTERIOR_REAR', x: 82, y: 50, severity: 'medium' },
  { id: '15', label: 'QUARTER_PANEL_PICK_UP_BOX_LEFT', x: 68, y: 75, severity: 'low' },
  { id: '17', label: 'QUARTER_PANEL_PICK_UP_BOX_RIGHT', x: 68, y: 24, severity: 'high' },
  { id: '72', label: 'LEFT_FRONT_TIRE', x: 30, y: 94, severity: 'high' },
  { id: '73', label: 'LEFT_FRONT_WHEEL_RIM', x: 33, y: 92, severity: 'low' },
  { id: '74', label: 'LEFT_REAR_TIRE', x: 70, y: 94, severity: 'medium' },
  { id: '75', label: 'LEFT_REAR_WHEEL_RIM', x: 66, y: 92, severity: 'medium' },
  { id: '76', label: 'RIGHT_REAR_TIRE', x: 70, y: 6, severity: 'high' },
  { id: '77', label: 'RIGHT_REAR_WHEEL_RIM', x: 66, y: 9, severity: 'high' },
  { id: '78', label: 'RIGHT_FRONT_TIRE', x: 30, y: 6, severity: 'medium' },
  { id: '79', label: 'RIGHT_FRONT_WHEEL_RIM', x: 33, y: 9, severity: 'low' },
  { id: '38', label: 'RUNNING_BOARD_STEP_LEFT', x: 52, y: 92, severity: 'low' },
  { id: '39', label: 'RUNNING_BOARD_STEP_RIGHT', x: 52, y: 8, severity: 'low' },
  { id: '87', label: 'A_PILLAR_CORNER_POST_FRONT_LEFT', x: 47, y: 35, severity: 'low' },
  { id: '88', label: 'A_PILLAR_CORNER_POST_FRONT_RIGHT', x: 47, y: 66, severity: 'medium' },
  { id: '69', label: 'CENTER_POST_RIGHT', x: 53, y: 32, severity: 'medium' },
  { id: '70', label: 'CENTER_POST_LEFT', x: 53, y: 72, severity: 'low' },
  { id: '71', label: 'CORNER_POST', x: 67, y: 66, severity: 'low' },
  { id: '47', label: 'CORNER_POST_PILLAR_REAR_RIGHT', x: 67, y: 34, severity: 'medium' },
];
